import { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { lightenDarkenColor, getContrastColor } from '../../lib/colorUtils';
import { useAuth } from '../../components/auth/AuthContext';
import { processMessageContent, getFilesByMimeType, hasFinalState } from '../../lib/mediaUtils';

import LoadingScreen from '../../components/chat/LoadingScreen';
import ErrorScreen from '../../components/chat/ErrorScreen';
import ChatHeader from '../../components/chat/ChatHeader';
import ChatMessages from '../../components/chat/ChatMessages';
import ChatInput from '../../components/chat/ChatInput';
import ChatDrawer from '../../components/chat/ChatDrawer';
import { chatService } from '../../services/PlatformService';

import { AlertCircle, Bot, MessageSquare } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AgentChat = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Get agent ID from URL
  const queryParams = new URLSearchParams(location.search);
  const agentId = queryParams.get('id') || params.id;

  // State for agent customizable settings
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [liveBrowserUse, setLiveBrowserUse] = useState(null);
  const [chatId, setChatId] = useState();
  const [conversationTitle, setConversationTitle] = useState('');
  
  // State to control chat drawer opening
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Get user metadata including photo
  const getUserMetadata = () => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return {
      user_name: currentUser?.name || userData.name || 'Usuário',
      photo_url: currentUser?.photo_url || userData.photo_url || null,
      user_id: currentUser?.id || userData.id || 'anonymous'
    };
  };
  
  // Function to fetch agent data from API
  const fetchAgentData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_BASE_URL}/agents/${agentId}`, { headers });
      
      if (response.data && response.data.data) {
        setAgent(response.data.data);
        setMessages([]);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching agent data:", err);
      if (err.response) {
        if (err.response.status === 404) {
          setError("Funcionário não encontrado. Ele pode ter sido removido ou você não tem permissão para acessá-lo.");
        } else if (err.response.status === 403) {
          setError("Você não tem permissão para conversar com este funcionário.");
        } else if (err.response.status === 401) {
          setError("É necessário fazer login para acessar este funcionário.");
          navigate('/login', { state: { from: location } });
        } else {
          setError(err.response.data?.error || "Erro ao carregar o funcionário");
        }
      } else {
        setError("Erro de conexão. Verifique sua internet e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear chat
  const handleClearChat = () => {
    setMessages([]);
    setChatId(undefined);
    setConversationTitle('');
  };

  // Create new chat
  const handleNewChat = () => {
    handleClearChat();
    setIsDrawerOpen(false);
  };

  // Handler to toggle drawer open/close
  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Handler to select a conversation
  const handleSelectChat = async (selectedChatId) => {
    setLoading(true);
    try {
      // Fetch chat details
      const chatData = await chatService.getChatById(selectedChatId);
      setConversationTitle(chatData.title || '');
      
      // Fetch messages
      const result = await chatService.getChatMessages(selectedChatId);

      let messagesLlm = [];
      
      for (let msg of result.messages) {
        const agentMessage = msg["agent_msg"]

        if (agentMessage.role === "assistant" || agentMessage.role === "user") {
          const { audioUrls, imageUrls, documentUrls, videoUrls } = getFilesByMimeType(msg?.metadata?.files || []);
          
          if (agentMessage?.content !== undefined) {
            messagesLlm.push({
              role: agentMessage.role,
              content: agentMessage?.content,
              imageUrls,
              audioUrls,
              documentUrls,
              videoUrls,
              files: msg?.metadata?.files || [],
              timestamp: msg.created_at
            })
          }
        }
      }

      setMessages(messagesLlm);
      setChatId(selectedChatId);
    } catch (error) {
      console.error('Error loading chat:', error);
      setError('Erro ao carregar a conversa. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Load agent data when component mounts
  useEffect(() => {
    if (agentId) {
      fetchAgentData();
    } else {
      setError("ID do funcionário não fornecido");
      setLoading(false);
    }
  }, [agentId]);
  
  // Apply dynamic CSS styles based on agent settings
  useEffect(() => {
    if (!agent || !agent.widget_settings) return;
    
    const widgetSettings = agent.widget_settings;
    
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=${widgetSettings.font_primary?.replace(' ', '+') || 'Inter'}:wght@400;500;600;700&family=${widgetSettings.font_secondary?.replace(' ', '+') || 'Roboto'}:wght@400;500&display=swap');
      
      .agent-chat-container {
        --primary-color: ${widgetSettings.primary_color || '#A020F0'};
        --secondary-color: ${widgetSettings.secondary_color || '#E0E7FF'};
        --primary-font: '${widgetSettings.font_primary || 'Inter'}', sans-serif;
        --secondary-font: '${widgetSettings.font_secondary || 'Roboto'}', sans-serif;
      }
      
      .agent-primary-button {
        background-color: var(--primary-color);
      }
      
      .agent-primary-button:hover {
        background-color: ${lightenDarkenColor(widgetSettings.primary_color || '#A020F0', -20)};
      }
      
      .agent-primary-bg {
        background-color: var(--primary-color);
      }
      
      .agent-secondary-bg {
        background-color: var(--secondary-color);
      }
      
      .agent-primary-text {
        color: var(--primary-color);
      }
      
      .agent-primary-border {
        border-color: var(--primary-color);
      }
      
      .agent-message-bubble {
        background-color: var(--secondary-color);
        color: ${getContrastColor(widgetSettings.secondary_color || '#E0E7FF')};
      }
    `;
    
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [agent]);

  const handleSendMessage = async (selectedDocuments = []) => {
    if (!inputMessage.trim() && selectedDocuments.length === 0) return;

    const userMessageContent = inputMessage;
    const userMetadata = getUserMetadata();
    
    // Generate title if it's a new conversation
    if (!conversationTitle && messages.length === 0) {
      const words = userMessageContent.split(' ').slice(0, 5).join(' ');
      setConversationTitle(words + (userMessageContent.split(' ').length > 5 ? '...' : ''));
    }
    
    setInputMessage('');
    setIsTyping(true);

    // Add user message to list
    setMessages(prev => [...prev, { 
      id: Date.now(),
      role: 'user', 
      content: userMessageContent,
      timestamp: new Date().toISOString(),
      files: selectedDocuments
    }]);

    // Add initial empty assistant message to be updated during streaming
    setMessages(prev => [...prev, { 
      id: Date.now() + 1,
      role: 'assistant', 
      content: '',
      timestamp: new Date().toISOString(),
      audioUrls: [],
      imageUrls: [],
      documentUrls: [],
      videoUrls: [],
      files: []
    }]);

    try {
      if (!agent) {
        throw new Error("Configuração do funcionário não disponível");
      }

      let chatData = {
        user_id: userMetadata.user_id,
        prompt: userMessageContent,
        agent_id: agentId,
        channel: "platform",
        chat_id: chatId,
        files: selectedDocuments,
        title: conversationTitle || userMessageContent.substring(0, 50),
        chat_metadata: {
          ...userMetadata,
          channel: "platform",
          platform_type: "web"
        }
      };
      
      if (chatId === undefined) {
        chatData.chat_id = new Date().toISOString()
        setChatId(chatData.chat_id)
      }

      const token = localStorage.getItem('authToken');
      
      const response = await fetch(API_BASE_URL + "/chat", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(chatData)
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao comunicar com o funcionário: ${response.status} ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let botResponse = '';
      
      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              const {
                audioUrls,
                imageUrls,
                documentUrls,
                videoUrls,
                allFiles
              } = processMessageContent(botResponse);
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                
                if (lastMessage.role === 'assistant') {
                  lastMessage.audioUrls = Array.isArray(audioUrls) ? [...audioUrls] : [];
                  lastMessage.imageUrls = Array.isArray(imageUrls) ? [...imageUrls] : [];
                  lastMessage.documentUrls = Array.isArray(documentUrls) ? [...documentUrls] : [];
                  lastMessage.videoUrls = Array.isArray(videoUrls) ? [...videoUrls] : [];

                  lastMessage.files = Array.isArray(allFiles) ? [...allFiles] : [];
                }
                return newMessages;
              });
              
              setIsTyping(false);
              break;
            }
            
            const chunk = decoder.decode(value);
            let processedChunk = chunk;
            
            botResponse += processedChunk;

            if (!hasFinalState(botResponse)) {
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.content = botResponse;
                  lastMessage.audioUrls = [];
                  lastMessage.imageUrls = [];
                  lastMessage.documentUrls = [];
                  lastMessage.videoUrls = [];
                  lastMessage.files = [];
                }
                return newMessages;
              });
            }
          }
        } catch (error) {
          console.error("Erro ao processar stream:", error);
          setIsTyping(false);
          
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.role === 'assistant' && !lastMessage.content) {
              lastMessage.content = "Ocorreu um erro ao comunicar com o funcionário. Por favor, tente novamente.";
            }
            return newMessages;
          });
        }
      };

      processStream();
      
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      setIsTyping(false);
      setMessages(prev => {
        if (prev[prev.length - 1]?.role === 'assistant' && !prev[prev.length - 1].content) {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1].content = `Erro: ${error.message || 'Ocorreu um erro desconhecido'}`;
          return newMessages;
        } else {
          return [...prev, { 
            id: Date.now() + 1,
            role: 'assistant', 
            content: `Erro: ${error.message || 'Ocorreu um erro desconhecido'}`,
            timestamp: new Date().toISOString(),
            audioUrls: []
          }];
        }
      });
    }
  };
  
  // Function to format timestamps
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Render loading screen
  if (loading) {
    return <LoadingScreen />;
  }
  
  // Render error screen
  if (error) {
    return (
      <ErrorScreen 
        error={error} 
        onBack={() => navigate('/my-agents')} 
        onRetry={fetchAgentData} 
      />
    );
  }
  
  // Render agent not found screen
  if (!agent) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
        <div className="text-center max-w-md p-8 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-yellow-100 to-orange-100">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Funcionário Não Encontrado</h2>
          <p className="text-lg text-gray-600 mb-6">Não foi possível encontrar o funcionário solicitado.</p>
          <button 
            className="px-6 py-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-2xl hover:from-green-700 hover:to-green-700 font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => navigate('/')}
          >
            Voltar para Início
          </button>
        </div>
      </div>
    );
  }
  
  const widgetSettings = agent.widget_settings || {};
  const layout = widgetSettings.layout || 'default';
  
  return (
    <div className={`flex h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 agent-chat-container agent-layout-${layout}`}>
      {/* Chat Drawer */}
      <ChatDrawer
        isOpen={isDrawerOpen}
        onToggle={toggleDrawer}
        agentId={agentId}
        currentChatId={chatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        primaryColor={widgetSettings.primary_color || '#A020F0'}
        chatService={chatService}
      />
      
      {/* Main Chat Area */}
      <div className={`flex ${liveBrowserUse ? 'w-[60%]' : 'w-full'} transition-all duration-300`}>
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <ChatHeader 
            agent={agent}
            conversationTitle={conversationTitle}
            onClearChat={handleClearChat}
            onNewChat={handleNewChat}
            onBack={() => navigate('/my-agents')}
            onToggleDrawer={toggleDrawer}
            hasMessages={messages.length > 0}
          />
          
          {/* Welcome Screen for Empty Chat */}
          {messages.length === 0 && (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-2xl">
                <div className="w-24 h-24 mx-auto mb-6 rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-100 to-green-100 p-1">
                  <div className="w-full h-full rounded-3xl overflow-hidden bg-white flex items-center justify-center">
                    {widgetSettings?.logo_url ? (
                      <img 
                        src={widgetSettings.logo_url} 
                        alt={agent.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Bot className="w-12 h-12 text-indigo-400" />
                    )}
                  </div>
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  Olá! Eu sou {agent.name}
                </h1>
                <p className="text-lg text-gray-600 mb-8">
                  {agent.description || 'Estou aqui para ajudar você. Como posso ser útil hoje?'}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-5 hover:shadow-lg transition-all cursor-pointer group">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-all">
                        <MessageSquare className="w-5 h-5 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-gray-800">Comece uma conversa</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      Digite sua mensagem abaixo para iniciar
                    </p>
                  </div>
                  
                  <div 
                    onClick={toggleDrawer}
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-all">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-gray-800">Conversas anteriores</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      Acesse suas conversas salvas
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Messages */}
          {messages.length > 0 && (
            <ChatMessages 
              agent={agent}
              messages={messages}
              isTyping={isTyping} 
              widgetSettings={widgetSettings} 
              formatTimestamp={formatTimestamp} 
            />
          )}
          
          {/* Input */}
          <ChatInput 
            inputMessage={inputMessage} 
            setInputMessage={setInputMessage} 
            handleSendMessage={handleSendMessage} 
            isTyping={isTyping} 
            widgetSettings={widgetSettings} 
          />
        </div>
      </div>

    </div>
  );
};

export default AgentChat;